import { CommonModule } from '@angular/common';
import { Component, HostListener } from '@angular/core';
import { NavBarComponent } from '../../../shared/components/nav-bar/nav-bar.component';
import { FormsModule } from '@angular/forms';

interface PriceLevel {
  price: string;
  shares: number;
  type: 'sell' | 'buy';
  color: string;
  minShares?: number;
  maxShares?: number;
}

interface MarketRow {
  id: number;
  title: string;
  image: string;
  maxSpread: string;
  minShares: number;
  reward: number;
  earnings: string;
  percent: string;
  yesPrice: string;
  noPrice: string;
  isExpanded: boolean;
  isSaved: boolean;
}

interface Order {
  id: number;
  action: 'Buy' | 'Sell';
  side: 'Yes' | 'No';
  type: 'Limit' | 'Market';
  price: string;
  shares: number;
  filled: number;
  total: number;
  expiry: string;
  timestamp: Date;
}

@Component({
  selector: 'app-rewards',
  imports: [NavBarComponent, CommonModule, FormsModule],
  templateUrl: './rewards.component.html',
  styleUrl: './rewards.component.css'
})
export class RewardsComponent {
  expandedRowIndex: number | null = null;
  activeSide: 'Buy' | 'Sell' = 'Buy';
  activeVote: 'Yes' | 'No' = 'Yes';
  earningsTooltipOpen = false;
  showMergeEmptyState = false;
  showSavedOnly = false;
  
  // Expiry states
  expiryToggleOn = false;
  expiryDropdownOpen = false;
  selectedExpiryOption: string = 'End of Day';
  selectedSide: 'yes' | 'no' = 'yes';
  
  // Tab states for right column
  activeTab: 'Order' | 'Position' | 'History' = 'Order';
  
  // Orders array
  orders: Order[] = [];
  orderIdCounter = 1;

  // Market data rows
  marketRows: MarketRow[] = [
    {
      id: 1,
      title: 'Will Trump disparage Ahmed al-Sharaa on Monday?',
      image: 'assets/images/home/card-image1.png',
      maxSpread: '±4¢',
      minShares: 50,
      reward: 150,
      earnings: '$0.00',
      percent: '-',
      yesPrice: '6¢',
      noPrice: '94¢',
      isExpanded: false,
      isSaved: false
    },
    {
      id: 2,
      title: 'Will Trump praise al-Sharaa on Monday?',
      image: 'assets/images/home/card-image2.png',
      maxSpread: '±4¢',
      minShares: 50,
      reward: 700,
      earnings: '$0.00',
      percent: '-',
      yesPrice: '95.5¢',
      noPrice: '4.5¢',
      isExpanded: false,
      isSaved: false
    },
    {
      id: 3,
      title: 'Will the government shutdown end November?',
      image: 'assets/images/details/government-shutdown.png',
      maxSpread: '±4¢',
      minShares: 50,
      reward: 600,
      earnings: '<$0.01',
      percent: '0.15%',
      yesPrice: '4¢',
      noPrice: '96¢',
      isExpanded: false,
      isSaved: false
    }
  ];

  get displayedMarketRows(): MarketRow[] {
    if (this.showMergeEmptyState) {
      return [];
    }

    return this.showSavedOnly
      ? this.marketRows.filter(row => row.isSaved)
      : this.marketRows;
  }

  // Sell price levels
  sellPriceLevels: PriceLevel[] = [
    { price: '11¢', shares: 0, type: 'sell', color: '#B27800' },
    { price: '10¢', shares: 0, type: 'sell', color: '#B27800' },
    { price: '9¢', shares: 0, type: 'sell', color: '#B27800' },
    { price: '8¢', shares: 0, type: 'sell', color: '#B27800' }
  ];

  // Buy price levels
  buyPriceLevels: PriceLevel[] = [
    { price: '5¢', shares: 0, type: 'buy', color: '#13987E' },
    { price: '4¢', shares: 0, type: 'buy', color: '#13987E' },
    { price: '3¢', shares: 0, type: 'buy', color: '#13987E' },
    { price: '', shares: 0, type: 'buy', color: '#000000' }
  ];

  // Expiry options with hours
  expiryHours = [3, 4, 6, 8, 12, 24];

  constructor() {
    this.buyPriceLevels.forEach(level => {
      level.minShares = 0;
    });
    
    this.sellPriceLevels.forEach(level => {
      level.maxShares = undefined;
    });
  }

  // Toggle expiry switch
  toggleExpirySwitch(): void {
    this.expiryToggleOn = !this.expiryToggleOn;
    if (!this.expiryToggleOn) {
      this.expiryDropdownOpen = false;
    }
  }

  toggleSide() {
    this.selectedSide = this.selectedSide === 'yes' ? 'no' : 'yes';
  }

  toggleSavedView(): void {
    this.showMergeEmptyState = false;
    this.showSavedOnly = !this.showSavedOnly;
  }

  toggleMergeEmptyState(): void {
    this.showSavedOnly = false;
    this.showMergeEmptyState = !this.showMergeEmptyState;
  }

  toggleSavedRow(row: MarketRow, event: Event): void {
    event.stopPropagation();
    row.isSaved = !row.isSaved;
  }

  toggleEarningsTooltip(event: Event): void {
    event.stopPropagation();
    this.earningsTooltipOpen = !this.earningsTooltipOpen;
  }

  // Toggle expiry dropdown menu
  toggleExpiryDropdown(): void {
    if (this.expiryToggleOn) {
      this.expiryDropdownOpen = !this.expiryDropdownOpen;
    }
  }

  // Select expiry option
  selectExpiryOption(option: string): void {
    this.selectedExpiryOption = option;
    this.expiryDropdownOpen = false;
  }

  // Switch tabs in right column
  switchTab(tab: 'Order' | 'Position' | 'History'): void {
    this.activeTab = tab;
  }

  // Place order - called when Place button is clicked
  placeOrder(): void {
    // Check if there are any shares entered in buy or sell levels
    const hasBuyOrders = this.buyPriceLevels.some(level => level.shares > 0);
    const hasSellOrders = this.sellPriceLevels.some(level => level.shares > 0);
    
    if (!hasBuyOrders && !hasSellOrders) {
      console.log('No orders to place');
      return;
    }
    
    // Create orders for buy levels with shares
    this.buyPriceLevels.forEach(level => {
      if (level.shares > 0) {
        const order: Order = {
          id: this.orderIdCounter++,
          action: 'Buy',
          side: this.selectedSide === 'yes' ? 'Yes' : 'No',
          type: 'Limit',
          price: level.price || 'Custom',
          shares: level.shares,
          filled: Math.floor(level.shares * 0.7), // Simulate 70% filled for demo
          total: this.calculateTotal(level.shares, level.price),
          expiry: this.getRandomExpiry(),
          timestamp: new Date()
        };
        this.orders.unshift(order); // Add to beginning of array
      }
    });
    
    // Create orders for sell levels with shares
    this.sellPriceLevels.forEach(level => {
      if (level.shares > 0) {
        const order: Order = {
          id: this.orderIdCounter++,
          action: 'Sell',
          side: this.selectedSide === 'yes' ? 'Yes' : 'No',
          type: 'Limit',
          price: level.price,
          shares: level.shares,
          filled: Math.floor(level.shares * 0.5), // Simulate 50% filled for demo
          total: this.calculateTotal(level.shares, level.price),
          expiry: this.getRandomExpiry(),
          timestamp: new Date()
        };
        this.orders.unshift(order);
      }
    });
    
    // Clear all shares after placing orders
    this.clearAllShares();
    
    console.log('Orders placed:', this.orders.length);
  }

  // Calculate total based on shares and price
  calculateTotal(shares: number, priceStr: string): number {
    // Extract numeric value from price string (e.g., "5¢" -> 5)
    const priceMatch = priceStr.match(/(\d+(\.\d+)?)/);
    const price = priceMatch ? parseFloat(priceMatch[1]) : 0;
    
    // Convert to dollars (assuming cents)
    const priceInDollars = price / 100;
    
    // Calculate total
    return shares * priceInDollars;
  }

  // Get random expiry time (for demo)
  getRandomExpiry(): string {
    const hours = this.expiryHours[Math.floor(Math.random() * this.expiryHours.length)];
    return `${hours} hours`;
  }

  // Cancel all orders
  cancelAllOrders(): void {
    this.orders = [];
    console.log('All orders cancelled');
  }

  removeOrder(orderId: number): void {
    this.orders = this.orders.filter(order => order.id !== orderId);
  }

  // Close dropdown when clicking outside
  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const isExpiryToggle = target.closest('.expiry-toggle-container');
    const isExpirySelect = target.closest('.expiry-select');
    const isExpiryDropdown = target.closest('.expiry-dropdown-menu');
    const isEarningsTooltip = target.closest('.earnings-tooltip');
    const isEarningsTooltipTrigger = target.closest('.earnings-tooltip-trigger');
    
    if (!isExpiryToggle && !isExpirySelect && !isExpiryDropdown) {
      this.expiryDropdownOpen = false;
    }

    if (!isEarningsTooltip && !isEarningsTooltipTrigger) {
      this.earningsTooltipOpen = false;
    }
  }

  // Clear all shares in both sections
  clearAllShares(): void {
    this.sellPriceLevels.forEach(level => {
      level.shares = 0;
      level.maxShares = undefined;
    });
    this.buyPriceLevels.forEach(level => {
      level.shares = 0;
      level.minShares = 0;
    });
  }

  // Increment all shares for a specific type
  incrementShares(type: 'sell' | 'buy'): void {
    const targetArray = type === 'sell' ? this.sellPriceLevels : this.buyPriceLevels;
    targetArray.forEach(level => {
      if (type === 'sell' && level.maxShares !== undefined) {
        level.shares = Math.min(level.maxShares, level.shares + 100);
      } else {
        level.shares += 100;
      }
    });
  }

  // Decrement all shares for a specific type
  decrementShares(type: 'sell' | 'buy'): void {
    const targetArray = type === 'sell' ? this.sellPriceLevels : this.buyPriceLevels;
    targetArray.forEach(level => {
      if (type === 'buy' && level.minShares !== undefined) {
        level.shares = Math.max(level.minShares, level.shares - 100);
      } else {
        level.shares = level.shares < 100 ? 0 : level.shares - 100;
      }
    });
  }

  // Set max shares for a specific price level (for SELL levels only)
  setMaxShares(level: PriceLevel): void {
    if (level.type === 'sell') {
      level.maxShares = level.shares;
      if (level.shares > level.maxShares) {
        level.shares = level.maxShares;
      }
    }
  }

  // Set min shares for a specific price level (for BUY levels only)
  setMinShares(level: PriceLevel): void {
    if (level.type === 'buy') {
      level.minShares = level.shares;
      if (level.shares < level.minShares) {
        level.shares = level.minShares;
      }
    }
  }

  // Increment shares for a specific level by 50
  incrementSharesForLevel(level: PriceLevel): void {
    if (level.type === 'sell' && level.maxShares !== undefined) {
      level.shares = Math.min(level.maxShares, level.shares + 50);
    } else {
      level.shares += 50;
    }
  }

  // Decrement shares for a specific level by 50
  decrementSharesForLevel(level: PriceLevel): void {
    if (level.type === 'buy' && level.minShares !== undefined) {
      level.shares = Math.max(level.minShares, level.shares - 50);
    } else {
      level.shares = Math.max(0, level.shares - 50);
    }
  }

  // Validate shares input
  validateShares(level: PriceLevel): void {
    if (isNaN(level.shares) || level.shares < 0) {
      level.shares = 0;
    }
    
    level.shares = Math.round(level.shares / 50) * 50;
    
    if (level.type === 'buy' && level.minShares !== undefined) {
      level.shares = Math.max(level.minShares, level.shares);
    } else if (level.type === 'sell' && level.maxShares !== undefined) {
      level.shares = Math.min(level.maxShares, level.shares);
    }
  }

  // Toggle row expansion (accordion behavior)
  toggleRow(row: MarketRow): void {
    this.marketRows.forEach(r => {
      if (r.id !== row.id) {
        r.isExpanded = false;
      }
    });
    
    row.isExpanded = !row.isExpanded;
  }

  // Format date for display
  formatDate(date: Date): string {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  // Format currency
  formatCurrency(amount: number): string {
    return '$' + (Number.isInteger(amount) ? amount.toFixed(0) : amount.toFixed(2));
  }
}
